import { faSquare, faSquareCheck } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import { Box, Button, ListItemIcon, Menu, MenuItem, Skeleton, Stack, useMediaQuery, useTheme } from "@mui/material";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { RefreshButton, StatusDot, useAppBar } from "../../../components";
import { Breadcrumb, Family } from "../../../models";
import { useGetAllFamiliesQuery } from "../../../redux/apiStore";
import * as pageState from "../../../redux/pageStateSlice";
import { EditFamilyDrawer } from "../edit";

function CheckableMenuItem(props: { children: React.ReactNode; checked: boolean; onClick: React.MouseEventHandler<HTMLLIElement> | undefined }) {
  const { children, checked, onClick } = props;

  return (
    <MenuItem onClick={onClick}>
      <>
        <ListItemIcon>{checked ? <FontAwesomeIcon icon={faSquareCheck} /> : <FontAwesomeIcon icon={faSquare} />}</ListItemIcon>
        {children}
      </>
    </MenuItem>
  );
}

function FiltersMenu() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const dispatch = useAppDispatch();
  const filters = useAppSelector(pageState.selectFamiliesFilters);
  const [editDrawerOpen, setEditDrawerOpen] = useState<boolean>(false);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggleShowInactive = () => {
    dispatch(pageState.setFamiliesFilters({ showInactive: !filters.showInactive }));
  };

  return (
    <>
      <EditFamilyDrawer open={editDrawerOpen} onClose={() => setEditDrawerOpen(false)} />
      <Stack gap={1} direction="row">
        <Button variant="outlined" startIcon={<FilterListIcon />} onClick={handleClick} sx={{ height: 50 }}>
          Filters
        </Button>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setEditDrawerOpen(true)} sx={{ height: 50 }}>
          Add
        </Button>
      </Stack>

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <CheckableMenuItem onClick={toggleShowInactive} checked={filters.showInactive}>
          Show Inactive
        </CheckableMenuItem>
      </Menu>
    </>
  );
}

function FamiliesTable() {
  const families = useGetAllFamiliesQuery({}); // moved to FamiliesPage for app bar refresh
  const navigate = useNavigate();
  const theme = useTheme();
  const filters = useAppSelector(pageState.selectFamiliesFilters);
  const orderedFilteredData = _.orderBy(families.data, [(x) => x.hasActiveChildren, (x) => x.name], ["desc", "asc"]).filter(
    (family) => filters.showInactive || family.hasActiveChildren,
  );
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isFetching = families.isFetching;

  const handleSelectFamily = (family: Family) => {
    navigate(`/families/${family.id}`);
  };

  const loadingRow = (
    <TableRow>
      <TableCell component="th" scope="row">
        <Stack direction="row" columnGap={1} alignItems="center">
          <StatusDot color={theme.palette.grey[400]} />
          <Skeleton width={200} height={30} />
        </Stack>
      </TableCell>
      {!isSmallScreen && (
        <TableCell>
          <Skeleton width={350} height={30} />
        </TableCell>
      )}
      <TableCell>
        <Skeleton width={200} height={30} />
      </TableCell>
    </TableRow>
  );

  return (
    <Stack gap={1}>
      <Stack direction="row">
        <Box flex="1 1 0"></Box>
        <FiltersMenu />
      </Stack>
      <TableContainer component={Paper}>
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              {!isSmallScreen && <TableCell>Address</TableCell>}
              <TableCell>Children</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isFetching && [loadingRow, loadingRow, loadingRow]}
            {orderedFilteredData?.map((family) => (
              <TableRow key={family.id} sx={{ cursor: "pointer" }} hover onClick={() => handleSelectFamily(family)}>
                <TableCell component="th" scope="row">
                  <Stack direction="row" columnGap={1} alignItems="center">
                    <StatusDot color={family.hasActiveChildren ? theme.palette.success.light : theme.palette.grey[400]} />
                    {family.name}
                  </Stack>
                </TableCell>
                {!isSmallScreen && (
                  <TableCell>
                    {family.address} {family.city}, {family.state} {family.zipCode}
                  </TableCell>
                )}
                <TableCell>
                  {_.orderBy(family.children, "firstName", "asc")
                    .map((child) => child.firstName)
                    .join(", ")}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

export function getFamiliesBreadcrumb(): Breadcrumb {
  return {
    title: "All Families",
    path: `/families`,
  };
}

export function FamiliesPage() {
  const appBar = useAppBar();
  const families = useGetAllFamiliesQuery({});

  useEffect(() => {
    appBar.setDefaults();
    appBar.setTitle("Families");
    appBar.setRightContent(<RefreshButton onRefresh={() => families.refetch()} />);
  }, []);

  return (
    <Box sx={{ p: 2 }}>
      <FamiliesTable />
    </Box>
  );
}
